import * as React from "react";
import { Box, useColorScheme } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";

import { dashboardConfig } from "@/config/dashboard";
import { Page as NotFoundPage } from "@/pages/not-found";

import { useAuth } from "../core/auth-provider";
import { Logo } from "../core/logo";
import { logoColors } from "../dashboard/layout/vertical/side-nav";
import { AccountPicker } from "./account-picker";

export interface AuthGuardProps {
	children: React.ReactNode;
}

export function AuthGuard({ children }: AuthGuardProps): React.JSX.Element | null {
	const { isLoading, isAuthenticated, setUser, account, user, permissions, setPermissions } = useAuth();
	const pathname = useLocation();

	const { colorScheme = "light" } = useColorScheme();

	const logoColor = logoColors[colorScheme]["blend_in"];

	const navigate = useNavigate();

	React.useEffect(() => {
		if (!localStorage.getItem("token")) {
			setUser(null);
			setPermissions({});
			navigate("/sign-in");

			return;
		}

		if (isLoading || isAuthenticated || localStorage.getItem("token")) {
			return;
		}

		setUser(null);
		setPermissions({});
		navigate("/sign-in");
	}, [isAuthenticated, isLoading, navigate, pathname, setPermissions, setUser]);

	if (isLoading || !isAuthenticated) {
		return (
			<Box
				sx={{
					width: "100%",
					height: "100%",
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					gap: 4,
				}}
			>
				<Logo height={60} width={222} color={logoColor} />
				<Box className="page-loader" />
			</Box>
		);
	}

	const navItems = dashboardConfig.navItems?.[0]?.items ?? [];
	const allNavItems = [
		...navItems.filter((item) => item.href && item.href !== "/"),
		...navItems.filter((item) => item.items).flatMap((item) => item.items?.filter((item) => item.href)),
	];

	const navItemsWithPermissions = allNavItems.filter((item) => item?.permission);
	const navItemsWithNeedAccount = allNavItems.filter((item) => item?.needAccount).map((item) => item?.href);
	const navItemsWithIsSuperAdmin = allNavItems
		.filter((item) => "isSuperAdmin" in item! && item?.isSuperAdmin)
		.map((item) => item?.href);

	const needsAccount =
		navItemsWithNeedAccount.some((route) => route && pathname.pathname.startsWith(route)) || pathname.pathname === "/";

	const isSuperAdmin = user?.isSuperAdmin;
	const needsSuperAdmin = navItemsWithIsSuperAdmin.some((route) => route && pathname.pathname.startsWith(route));

	const currentPermissionNavItem = navItemsWithPermissions.find(
		(item) => item?.href && pathname.pathname.startsWith(item.href)
	);
	const needsPermission =
		currentPermissionNavItem?.permission && !permissions[currentPermissionNavItem?.permission ?? ""];

	if ((pathname.pathname === "/accounts" || pathname.pathname === "/") && !isSuperAdmin && !account) {
		if (pathname.pathname !== "/") {
			navigate("/", { replace: true });
			return null;
		}
		return <AccountPicker />;
	}

  if (!account && isSuperAdmin && pathname.pathname === "/") {
    navigate("/accounts", { replace: true });
    return null;
  }

	if ((!account && needsAccount) || (!isSuperAdmin && needsSuperAdmin) || needsPermission) {
		return <NotFoundPage />;
	}

	return <React.Fragment>{children}</React.Fragment>;
}
